/**
 * A hand-rolled inline-SVG column chart for a time series (submissions or page
 * views per day). No chart dependency. Thin poster-red columns on white, a
 * single hairline baseline, and the date range spelled out beneath -- per-column
 * labels would be unreadable at 30+ points, so only the endpoints are shown.
 *
 * Every column height is a share of the real max; an all-zero series draws a
 * flat baseline rather than dividing by zero.
 */

export interface ColumnPoint {
  /** Full label for the accessible description (e.g. the ISO date). */
  label: string;
  value: number;
}

const WIDTH = 640;
const HEIGHT = 140;
const GAP = 2;

export function ColumnChart({
  data,
  caption,
}: {
  data: ColumnPoint[];
  caption: string;
}) {
  const max = Math.max(1, ...data.map((d) => d.value));
  const n = Math.max(1, data.length);
  const barW = (WIDTH - GAP * (n - 1)) / n;
  const total = data.reduce((sum, d) => sum + d.value, 0);

  const first = data[0]?.label ?? '';
  const last = data[data.length - 1]?.label ?? '';

  return (
    <figure className="chart">
      <figcaption className="chart__caption">{caption}</figcaption>
      <svg
        className="chart__svg chart__svg--columns"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${caption}. ${total} in total across ${data.length} days.`}
      >
        {data.map((d, i) => {
          const h = (d.value / max) * (HEIGHT - 2);
          const x = i * (barW + GAP);
          const y = HEIGHT - h;
          return (
            <rect
              key={d.label}
              className="chart__col"
              x={x}
              y={y}
              width={barW}
              height={Math.max(h, d.value > 0 ? 1 : 0)}
            >
              <title>{`${d.label}: ${d.value}`}</title>
            </rect>
          );
        })}
      </svg>
      <div className="chart__axis">
        <span>{first}</span>
        <span>{last}</span>
      </div>
    </figure>
  );
}
